/**
 * StatStrip — shared full-width stat row for the blog detail tabs.
 *
 * One presentational component for the Progress tab's generation stats and
 * the Details & QA tab's metadata row, so the two read as the same visual
 * language: small uppercase label, large tabular-numeral value, hairline
 * dividers, spanning the available width.
 */

import React from 'react';

export interface StatItem {
  key: string;
  label: string;
  /** Rendered large; keep numerals tabular-friendly (plain text or chips) */
  value: React.ReactNode;
  /** Optional quiet suffix rendered after the value (e.g. "of 20") */
  hint?: string;
}

interface StatStripProps {
  items: StatItem[];
  className?: string;
}

const StatStrip: React.FC<StatStripProps> = ({ items, className }) => {
  if (items.length === 0) return null;

  return (
    <div
      className={`flex flex-wrap divide-x divide-border rounded-lg border border-border bg-card shadow-sm ${className ?? ''}`}
    >
      {items.map((item) => (
        <div key={item.key} className="flex-1 min-w-[9rem] px-4 py-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            {item.label}
          </div>
          <div className="mt-1 text-base font-semibold text-foreground tabular-nums leading-snug break-words">
            {item.value}
            {item.hint && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">{item.hint}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatStrip;
